import { useQuery } from "@apollo/client";
import { useLocalStorage, deleteFromStorage } from "@rehooks/local-storage";

import useTypedContext from "hooks/useTypedContext";
import { SavedFilter } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { getFiltersFromSavedData } from "./SavedViews/helpers";
import { DEFAULT_VIEW_ID } from "./constants";
import { SAVED_VIEW } from "./gql";
import { getDefaultViewStorageKey } from "./helpers";
import { DefaultView, FilterItemSettings, SavedFilterView } from "./types";

export const useDefaultView = (
  filtersOrder: FilterItemSettings[],
  filtersType: string,
  defaultEmptyFilterView: SavedFilterView
): DefaultView => {
  const { viewer } = useTypedContext(AccountContext);

  const defaultViewIdStorageKey = getDefaultViewStorageKey(filtersType);

  const [defaultViewId] = useLocalStorage<string>(defaultViewIdStorageKey);

  const { loading: defaultFilterLoading, data: defaultFilterData } = useQuery<{
    savedFilter: SavedFilter;
  }>(SAVED_VIEW, {
    variables: {
      id: defaultViewId,
    },
    skip: !defaultViewId || defaultViewId === DEFAULT_VIEW_ID,
    // APOLLO CLIENT UPDATE notes
  });

  const removeFromStorage = () => {
    deleteFromStorage(defaultViewIdStorageKey);
  };

  if (defaultFilterLoading || !filtersOrder.length) {
    return {
      loading: defaultFilterLoading,
      data: undefined,
      rawData: "",
      removeFromStorage,
    };
  }

  if (!defaultViewId || !defaultFilterData || !defaultFilterData.savedFilter) {
    const defaultValues = {
      loading: defaultFilterLoading,
      data: defaultEmptyFilterView,
      removeFromStorage,
    };

    try {
      const rawData = JSON.stringify({
        filters: [],
        order: defaultEmptyFilterView.order,
        text: "",
        sort: {},
      });

      return {
        ...defaultValues,
        rawData,
      };
    } catch {
      return {
        ...defaultValues,
        rawData: "",
      };
    }
  }

  return {
    loading: defaultFilterLoading,
    data: {
      ...getFiltersFromSavedData(defaultFilterData.savedFilter.data, defaultEmptyFilterView),
      id: defaultFilterData.savedFilter.id,
      name: defaultFilterData.savedFilter.name,
      isPublic: defaultFilterData.savedFilter.isPublic,
      isMine: defaultFilterData.savedFilter.createdBy === viewer.id,
    },
    rawData: defaultFilterData.savedFilter.data,
    removeFromStorage,
  };
};
